import { z } from 'zod';
// Notification framework — spec §1 ("Mag lives in Slack/web/email"), §6
// (the Weekly Cadence is Mag's notification rhythm), §5 (authority model
// drives whether Mag asks or just acts).
//
// This file is the source of truth for the *catalog* — every kind of
// notification Mag (or the system) can send — plus the per-kind payload
// schemas, channel enum, priority enum, and category. The DB stores
// `kind` as plain text so we don't have to migrate the enum every time
// we add a kind; this catalog validates on the way in.
/* ────────────────────────────────────────────────────────────────────
 * Channels & priorities
 *
 * Stable enums — these change rarely, and changing them is a real
 * product decision (new channel needs an adapter, new priority needs
 * UI treatment). Mirrored to pgEnums in @magig/db.
 * ──────────────────────────────────────────────────────────────────── */
export const notificationChannelSchema = z.enum([
    'in_app',
    'email',
    'slack',
    'web_push',
    'telegram',
]);
export const NOTIFICATION_CHANNELS = [
    'in_app',
    'email',
    'slack',
    'web_push',
    'telegram',
];
export const notificationPrioritySchema = z.enum(['low', 'normal', 'high', 'urgent']);
export const notificationCategorySchema = z.enum([
    // Auth / transactional — required, ignores prefs.
    'auth',
    // §6 cadence — Mag's weekly rhythm (Recap / Drops / Brief / Plan).
    'cadence',
    // Spend caps, anomaly spikes, things that need eyes soon.
    'alert',
    // Mag needs a founder answer or approval to proceed (§5 A2/A3).
    'ask',
    // Celebrations — a campaign hit a milestone, ROAS topped a target.
    'win',
    // Product announcements, system updates from the team.
    'system',
]);
export const notificationStatusSchema = z.enum(['pending', 'dispatched', 'partial', 'failed']);
export const notificationDeliveryStatusSchema = z.enum([
    'queued',
    'sending',
    'sent',
    'delivered',
    'failed',
    'skipped',
    /** [spec 56a42bdf] Withheld to be coalesced into a digest. NOT 'queued'
     *  (nothing picks it up until hold_until passes) and NOT 'skipped' (it
     *  will be sent; a skip never is). Mirrors the pg enum, which appends it
     *  for the same reason. */
    'held',
]);
// Catalog ids — listed first as a const tuple so the zod enum below
// can derive its type without circular imports.
export const NOTIFICATION_KIND_IDS = [
    // ── auth ──────────────────────────────────────────────────────────
    'auth.magic_link',
    'auth.reset_password',
    'auth.welcome',
    // ── cadence (§6) ──────────────────────────────────────────────────
    'cadence.recap_monday',
    'cadence.creative_drop',
    'cadence.brief_friday',
    'cadence.plan_monthly',
    'cadence.memo',
    'cadence.daily_ping',
    // [s221] Mag's reads & insights — the informational cards she lands
    // on /home (seo audit, funnel diagnosis, analytics finding, campaign
    // insight, competition landscape, instagram reads). The generic
    // feed-card notifier routes these here so the founder gets pinged
    // when mag notices something, configurable as one toggle.
    'cadence.insight',
    // [8140542] Setup nudges — the cards mag lands to get a founder un-stuck
    // before anything is running ("brief me on your first campaign", "drop the
    // tag on your site"). Deliberately its own kind rather than reusing
    // ask.input or cadence.insight: see the catalog entry for why both were
    // wrong.
    'cadence.nudge',
    // A scheduled post just went live on an organic channel. Fired by the
    // auto-publish scheduler (not manual "publish now" — the founder's
    // already watching then). Lets the founder hear "your post is up"
    // wherever they live, without opening the dashboard.
    'cadence.post_published',
    // ── alerts ────────────────────────────────────────────────────────
    'alert.spend',
    'alert.anomaly',
    'alert.event_cap',
    // [s264] Mag-tokens billing alerts. tokens_low fires when balance
    // crosses the warning threshold; tokens_zero fires when mag's
    // generative side has to pause. autobuy_charged is a positive
    // confirmation; autobuy_failed prompts the founder to fix their
    // payment method before mag pauses again. See
    // the mag-tokens doc (CommitMind) §6.
    'billing.tokens_low',
    'billing.tokens_zero',
    'billing.autobuy_charged',
    'billing.autobuy_failed',
    // [12.2L-112] Google Ads policy violation detected. Fires when
    // detection finds a newly-disapproved ad on an imported campaign.
    // Defaults to every channel because policy issues halt serving
    // and the founder needs to know fast (or trust mag to fix it).
    'alert.policy_issue',
    // [spec c785b77b] Mag DID something on her own and this is the receipt.
    // Fires after an autonomous action executes, not before — the grace-window
    // heads-up is a separate send that reuses ask.approval.
    //
    // ITS OWN KIND RATHER THAN cadence.insight, deliberately. An insight is
    // something mag noticed and the founder can ignore; this is something she
    // changed, and the first one is an RSA swap that REMOVES the live ad with no
    // way back. Routing it through the insights toggle would let a founder who
    // muted insights never learn their ad had been replaced, which is the one
    // outcome this notification exists to prevent. Same reasoning as
    // alert.policy_issue: it defaults to every channel because the founder needs
    // to know, or trusting mag stops being reasonable.
    'alert.autonomous_action',
    // [12.2L-121] AI strategist proposal — mag has a campaign idea worth
    // your time. Normal priority (not high — a missed proposal isn't an
    // outage, just a slower iteration cycle), in-app + email default.
    // Slack is opt-in.
    'alert.strategy_proposal',
    // ── asks (§5) ─────────────────────────────────────────────────────
    'ask.input',
    'ask.approval',
    // ── wins ──────────────────────────────────────────────────────────
    'win.milestone',
    // ── system ────────────────────────────────────────────────────────
    'system.announcement',
];
export const notificationKindSchema = z.enum(NOTIFICATION_KIND_IDS);
/** The full catalog, frozen at module load. Keys = kind ids. */
export const NOTIFICATION_CATALOG = Object.freeze({
    // Auth — required, email-only. Don't let a user disable their own
    // magic-link delivery.
    'auth.magic_link': {
        id: 'auth.magic_link',
        category: 'auth',
        defaultChannels: ['email'],
        defaultPriority: 'high',
        required: true,
        label: 'magic link',
        description: "the link you tap to sign in. you can't turn this one off.",
    },
    'auth.reset_password': {
        id: 'auth.reset_password',
        category: 'auth',
        defaultChannels: ['email'],
        defaultPriority: 'high',
        required: true,
        label: 'password reset',
        description: "how you reset your password. you can't turn this one off.",
    },
    'auth.welcome': {
        id: 'auth.welcome',
        category: 'auth',
        defaultChannels: ['email'],
        defaultPriority: 'normal',
        required: true,
        label: 'welcome',
        description: 'the note i send when you first arrive. one-time.',
    },
    // Cadence — §6. Default to in-app + slack so the founder sees them
    // in their working surface; email as a backup catch.
    'cadence.recap_monday': {
        id: 'cadence.recap_monday',
        category: 'cadence',
        defaultChannels: ['in_app', 'slack', 'email'],
        defaultPriority: 'normal',
        required: false,
        label: 'monday recap',
        description: "your weekend numbers + what i'm doing this week. monday 9am.",
    },
    'cadence.creative_drop': {
        id: 'cadence.creative_drop',
        category: 'cadence',
        defaultChannels: ['in_app', 'slack'],
        defaultPriority: 'normal',
        required: false,
        label: 'creative drops',
        description: 'new concepts for you to glance over. tues + thurs at 11am.',
    },
    'cadence.brief_friday': {
        id: 'cadence.brief_friday',
        category: 'cadence',
        defaultChannels: ['in_app', 'slack', 'email'],
        defaultPriority: 'normal',
        required: false,
        label: 'friday brief',
        description: 'honest week wrap — what worked, what i got wrong. friday 4pm.',
    },
    'cadence.plan_monthly': {
        id: 'cadence.plan_monthly',
        category: 'cadence',
        defaultChannels: ['in_app', 'slack', 'email'],
        defaultPriority: 'normal',
        required: false,
        label: 'monthly plan',
        description: 'the next 30 days. first monday of the month.',
    },
    // The memo (spec §0a.3 — the canonical weekly artifact). Defaults
    // include email because the memo is the "agency feel" deliverable
    // founders may read in their inbox before opening the dashboard.
    'cadence.memo': {
        id: 'cadence.memo',
        category: 'cadence',
        defaultChannels: ['in_app', 'slack', 'email'],
        defaultPriority: 'normal',
        required: false,
        label: 'weekly memo',
        description: "the week's memo — how i read it, what i shipped, what i need from you.",
    },
    // The daily ping (spec §0a.5 — between-memo proactive push).
    // Defaults SKIP email — daily pings are lightweight contextual
    // nudges; landing them in a separate inbox channel from the
    // memo prevents email fatigue. Anti-noise gate inside the
    // scheduler keeps frequency low.
    'cadence.daily_ping': {
        id: 'cadence.daily_ping',
        category: 'cadence',
        defaultChannels: ['in_app', 'slack', 'telegram'],
        defaultPriority: 'normal',
        required: false,
        label: 'daily ping',
        description: 'quick note when something moved — only on days worth a note.',
    },
    // [s221] Mag's reads & insights — informational cards landing on
    // /home (seo audit, funnel diagnosis, analytics finding, etc.). In-app
    // + web-push + email by default so the founder hears when mag notices
    // something even while away; one toggle to mute the lot.
    'cadence.insight': {
        id: 'cadence.insight',
        category: 'cadence',
        defaultChannels: ['in_app', 'web_push', 'email'],
        defaultPriority: 'normal',
        required: false,
        label: "mag's reads & insights",
        description: 'when i notice something on your site, funnel, or analytics worth a look.',
    },
    // [8140542] Setup nudges. in_app ONLY, and that is the whole point of the
    // kind existing.
    //
    // These two cards — "ready when you are" and "one tag and i can see what
    // your site is doing" — were classified ask.input, so they arrived at HIGH
    // priority wearing a warning triangle, over the onboarding wizard. Neither
    // is an alarm; one is an invitation and the other is a setup step the
    // wizard is itself in the middle of.
    //
    // The obvious repair was cadence.insight, and it is a trap. That kind
    // defaults to ['in_app','web_push','email'], so moving the nudges there
    // fixes the icon and starts EMAILING the founder "ready when you are" the
    // moment the 30-minute onboarding quiet window lapses — which is the
    // four-emails-in-twenty-five-minutes flood spec 006abee3 exists to kill,
    // reintroduced just outside the window where nothing catches it.
    //
    // So: the bell, and nothing else. A nudge is worth seeing when the founder
    // looks. It has not earned an interrupt and it has never earned an inbox.
    'cadence.nudge': {
        id: 'cadence.nudge',
        category: 'cadence',
        defaultChannels: ['in_app'],
        defaultPriority: 'normal',
        required: false,
        label: 'setup nudges',
        description: "when something's not wired up yet and i can't get going without it.",
    },
    // A scheduled post went live. Real-time channels only (in-app +
    // push + whichever chat the founder lives in) — deliberately NO
    // email, because posts ship on a cadence and an inbox hit per post
    // is fatigue, same reasoning as cadence.daily_ping.
    'cadence.post_published': {
        id: 'cadence.post_published',
        category: 'cadence',
        defaultChannels: ['in_app', 'web_push', 'slack', 'telegram'],
        defaultPriority: 'normal',
        required: false,
        label: 'post went live',
        description: 'a quick heads-up the moment one of your posts publishes.',
    },
    // Alerts — high priority by default; in-app + slack so the founder
    // sees them before email lands.
    'alert.spend': {
        id: 'alert.spend',
        category: 'alert',
        defaultChannels: ['in_app', 'slack', 'email'],
        defaultPriority: 'high',
        required: false,
        label: 'spend alerts',
        description: 'when spend approaches or crosses a cap you set.',
    },
    'alert.anomaly': {
        id: 'alert.anomaly',
        category: 'alert',
        defaultChannels: ['in_app', 'slack'],
        defaultPriority: 'high',
        required: false,
        label: 'anomaly alerts',
        description: 'sudden spikes or drops i noticed in your data. fresh, not stale.',
    },
    // [s264] Mag-tokens balance alerts. tokens_low + tokens_zero are
    // billing-critical (the founder needs to act or mag pauses), so
    // they're high priority and default to in_app + email. Slack
    // opt-in (working channels don't need balance pings). autobuy_*
    // are confirmation/failure notes — normal priority, same channels
    // since they're billing-related.
    'billing.tokens_low': {
        id: 'billing.tokens_low',
        category: 'alert',
        defaultChannels: ['in_app', 'email'],
        defaultPriority: 'high',
        required: false,
        label: 'low token balance',
        description: "when you're running low on mag tokens and i'll pause soon if you don't top up.",
    },
    'billing.tokens_zero': {
        id: 'billing.tokens_zero',
        category: 'alert',
        defaultChannels: ['in_app', 'email'],
        defaultPriority: 'high',
        required: false,
        label: 'tokens depleted',
        description: 'when my generative side has to pause until you top up or auto-buy fires.',
    },
    'billing.autobuy_charged': {
        id: 'billing.autobuy_charged',
        category: 'alert',
        defaultChannels: ['in_app', 'email'],
        defaultPriority: 'normal',
        required: false,
        label: 'auto-buy confirmations',
        description: 'when i top up your tokens automatically — receipt + new balance.',
    },
    'billing.autobuy_failed': {
        id: 'billing.autobuy_failed',
        category: 'alert',
        defaultChannels: ['in_app', 'email'],
        defaultPriority: 'high',
        required: false,
        label: 'auto-buy failures',
        description: "when an auto-buy charge fails. fix your card or i'll pause on the next dip.",
    },
    // s212 — analytics-event cap warnings + over-cap notifications.
    // Fires when the org crosses 80% of its plan's
    // monthlyAnalyticsEvents (heads-up) and at 100% (analytics paused
    // until reset). High priority + in_app + email so the founder
    // catches it before ingest silently pauses on their site.
    'alert.event_cap': {
        id: 'alert.event_cap',
        category: 'alert',
        defaultChannels: ['in_app', 'email'],
        defaultPriority: 'high',
        required: false,
        label: 'event cap alerts',
        description: "when you're approaching or have hit your plan's monthly event cap.",
    },
    // [12.2L-112] Policy-issue alert. Default to every channel —
    // disapproved ads stop serving, so every minute of delay is real
    // spend that could be running. Founder needs to know fast OR
    // trust mag is auto-fixing it (Phase 2).
    'alert.policy_issue': {
        id: 'alert.policy_issue',
        category: 'alert',
        defaultChannels: ['in_app', 'email', 'slack'],
        defaultPriority: 'high',
        required: false,
        label: 'ad policy alerts',
        description: "when google rejects an ad on one of your campaigns. i'll tell you what + why.",
    },
    'alert.autonomous_action': {
        id: 'alert.autonomous_action',
        category: 'alert',
        defaultChannels: ['in_app', 'email', 'slack'],
        defaultPriority: 'high',
        // NOT required: a founder may legitimately decide they trust mag and
        // would rather read the campaign's action log than get pinged. But it is
        // on by default everywhere, because the alternative is a permanent
        // change to a live ad that nobody was told about.
        required: false,
        label: 'things i did on my own',
        description: 'when i changed something myself — what i did, and why i thought it was right.',
    },
    // [12.2L-121] AI strategist proposals. Normal priority — these are
    // ideas, not outages. Default in_app + email so the founder reads
    // them on /home or in their inbox; slack opt-in to avoid bumping
    // working-channel traffic.
    'alert.strategy_proposal': {
        id: 'alert.strategy_proposal',
        category: 'alert',
        defaultChannels: ['in_app', 'email'],
        defaultPriority: 'normal',
        required: false,
        label: 'strategy proposals',
        description: 'when i found a campaign worth running — accept or pass on it.',
    },
    // Asks — §5 A2/A3. When mag needs you, she needs you.
    'ask.input': {
        id: 'ask.input',
        category: 'ask',
        defaultChannels: ['in_app', 'slack'],
        defaultPriority: 'high',
        required: false,
        label: 'asks',
        description: 'when i need an answer from you to keep going.',
    },
    'ask.approval': {
        id: 'ask.approval',
        category: 'ask',
        defaultChannels: ['in_app', 'slack', 'email'],
        defaultPriority: 'high',
        required: false,
        label: 'approvals',
        description: "when i need your go-ahead on something i can't decide alone.",
    },
    // Wins — celebrate sparingly. In-app + email — slack risks fatigue.
    'win.milestone': {
        id: 'win.milestone',
        category: 'win',
        defaultChannels: ['in_app', 'email'],
        defaultPriority: 'normal',
        required: false,
        label: 'wins',
        description: 'when something crossed a target worth telling you about.',
    },
    // System — product / team messages. Quiet by default.
    'system.announcement': {
        id: 'system.announcement',
        category: 'system',
        defaultChannels: ['in_app'],
        defaultPriority: 'low',
        required: false,
        label: 'product updates',
        description: 'occasional notes from the magig team about the product.',
    },
});
/** All kinds as an array — convenient for the prefs UI to iterate. */
export const NOTIFICATION_KINDS = Object.freeze(NOTIFICATION_KIND_IDS.map((id) => NOTIFICATION_CATALOG[id]));
/** Lookup helper. Throws on unknown id — call sites should validate
 *  with `notificationKindSchema` first. */
export function getNotificationKind(id) {
    return NOTIFICATION_CATALOG[id];
}
/* ────────────────────────────────────────────────────────────────────
 * CTA + per-kind payload schemas
 *
 * The notification row carries `title` + `body` (mag-voice strings) on
 * its own columns. `payload` is per-kind structured data the UI needs
 * for rendering or that channel adapters consume (feedCardId, campaign
 * pointers, etc). Keep payloads small and discriminated by `kind`.
 * ──────────────────────────────────────────────────────────────────── */
export const notificationCtaSchema = z.object({
    /** Lowercase per §1. Max ~30 chars so it fits in a slack button +
     *  toast action. */
    label: z.string().min(1).max(30),
    /** Relative path ('/brand/foo/notifications/abc') or absolute URL.
     *  Validated as URL when absolute. */
    href: z.string().min(1).max(2048),
});
// Base shape every payload extends — the `kind` discriminator lives at
// the same key as the catalog id, and an optional feed_card_id pointer
// links the notification to the work surface it's about.
const basePayloadShape = {
    feedCardId: z.string().uuid().optional(),
    /** [spec 6bfa5da5] The same idea as feedCardId, for the work surface
     *  that is NOT a card: a row in pending_autonomous_actions sitting in
     *  its grace window. The chat adapters follow this pointer to mint a
     *  tap-to-act button, exactly as they follow feedCardId.
     *
     *  It lives on the SHARED base rather than in an ask.approval-specific
     *  payload because ask.approval is the kind for approval asks
     *  generally — other senders use it and have no action to describe. */
    pendingActionId: z.string().uuid().optional(),
};
// Most kinds carry no extra structure today — title/body on the row is
// enough. They each get their own literal so the discriminated union
// stays sound (and future structure can be added without a wire break).
const emptyPayload = (kind) => z.object({ kind: z.literal(kind), ...basePayloadShape });
/** Magic-link auth payload — carries the Better Auth-generated URL so
 *  the email adapter can render the sign-in button. expiresInMinutes
 *  matches Better Auth's magicLink default (5m); the adapter renders
 *  it as fallback copy ("link expires in 5 minutes"). */
export const authMagicLinkPayloadSchema = z.object({
    kind: z.literal('auth.magic_link'),
    ...basePayloadShape,
    url: z.string().url(),
    expiresInMinutes: z.number().int().positive().optional(),
});
/** Reset-password auth payload — same shape as magic-link. */
export const authResetPasswordPayloadSchema = z.object({
    kind: z.literal('auth.reset_password'),
    ...basePayloadShape,
    url: z.string().url(),
    expiresInMinutes: z.number().int().positive().optional(),
});
/** Welcome auth payload — sent on user creation. connectUrl is the
 *  "connect your ad accounts" landing page; firstName is rendered
 *  lowercase by the template per §1. */
export const authWelcomePayloadSchema = z.object({
    kind: z.literal('auth.welcome'),
    ...basePayloadShape,
    firstName: z.string().min(1).max(80),
    connectUrl: z.string().url(),
});
export const cadenceRecapMondayPayloadSchema = emptyPayload('cadence.recap_monday');
export const cadenceCreativeDropPayloadSchema = emptyPayload('cadence.creative_drop');
export const cadenceBriefFridayPayloadSchema = emptyPayload('cadence.brief_friday');
export const cadencePlanMonthlyPayloadSchema = emptyPayload('cadence.plan_monthly');
/** Memo payload — spec §0a.3. Carries the brand_memos.id so renderers
 *  + the dashboard's deep-link can fetch the full memo. Distinct from
 *  feedCardId because memos write to brand_memos, not feed_cards.
 *
 *  feedCardId is OPTIONAL on the memo payload: it points at the hero
 *  ask's underlying feed_card when one exists. The slack + telegram
 *  adapters' chat-actions integration reads this to render inline
 *  approve/dismiss buttons next to the memo notification so the
 *  founder can clear the hero ask straight from chat.  */
export const cadenceMemoPayloadSchema = z.object({
    kind: z.literal('cadence.memo'),
    ...basePayloadShape,
    brandMemoId: z.string().uuid(),
});
/** Daily ping payload — spec §0a.5. Carries the brand_daily_pings.id
 *  + the trigger kind so adapters/clients can render the ping with
 *  the right accent. Like the memo payload, feedCardId is optional —
 *  set when the ping was triggered by a new hero ask card so chat
 *  adapters can render inline buttons. */
export const cadenceDailyPingPayloadSchema = z.object({
    kind: z.literal('cadence.daily_ping'),
    ...basePayloadShape,
    brandDailyPingId: z.string().uuid(),
    triggerKind: z.enum(['budget_escalated', 'metric_milestone', 'new_hero_ask']),
});
// [s221] Insight notification — just points back at the feed card it's about
// (feedCardId on the base shape); title/body come from the card row.
export const cadenceInsightPayloadSchema = emptyPayload('cadence.insight');
export const cadenceNudgePayloadSchema = emptyPayload('cadence.nudge');
/** Post-published payload — carries the slot id + which organic channel
 *  it went out on so clients/adapters can deep-link to the slot. permalink
 *  is set when the platform returns one (mock publishes have none). */
export const cadencePostPublishedPayloadSchema = z.object({
    kind: z.literal('cadence.post_published'),
    ...basePayloadShape,
    slotId: z.string().uuid(),
    channel: z.enum(['instagram', 'linkedin', 'tiktok', 'youtube']),
    permalink: z.string().url().optional(),
});
/** Spend alert payload — channel adapters (email/slack) render the
 *  cap + current spend inline so the founder gets the numbers without
 *  clicking through. */
export const alertSpendPayloadSchema = z.object({
    kind: z.literal('alert.spend'),
    ...basePayloadShape,
    /** ISO 4217 + minor-units integer so we don't carry float rounding. */
    currentSpend: z.object({ currency: z.string().length(3), amountMinor: z.number().int().min(0) }),
    cap: z.object({ currency: z.string().length(3), amountMinor: z.number().int().min(0) }),
    /** 0..1 — share of cap currently consumed. Lets the UI render a
     *  bar without recomputing from currentSpend/cap. */
    share: z.number().min(0).max(2),
    campaignId: z.string().uuid().optional(),
});
/** Anomaly alert payload — mirrors the campaign anomaly_hint jsonb
 *  shape so the framework can pass through what the watcher saw. */
export const alertAnomalyPayloadSchema = z.object({
    kind: z.literal('alert.anomaly'),
    ...basePayloadShape,
    anomalyKind: z.enum(['spike', 'drop', 'shift', 'funnel_break']),
    eventName: z.string().min(1).max(120),
    campaignId: z.string().uuid().optional(),
});
/** Event-cap alert payload (s212) — carries the cap + current usage
 *  so channel adapters can render the bar / numbers inline without a
 *  round-trip to /api/billing/analytics-usage. `stage` lets the
 *  receiving UI / email render the "approaching" vs "reached" copy
 *  variant without re-deriving from share. */
/** [12.2L-112] Policy-issue alert payload. Carries enough for the
 *  channel adapters to render the alert without re-querying — campaign
 *  + ad context, the topic string, severity type, and a one-line
 *  mag-voice summary the founder reads at a glance. */
export const alertPolicyIssuePayloadSchema = z.object({
    kind: z.literal('alert.policy_issue'),
    ...basePayloadShape,
    campaignId: z.string().uuid(),
    campaignName: z.string().min(1).max(200),
    /** providerAdId from the snapshot. Lets the email/slack adapters
     *  link straight into Google Ads if they want. */
    providerAdId: z.string().min(1).max(120),
    /** Google's PolicyTopicEntry.topic ("DESTINATION_NOT_WORKING"). */
    topic: z.string().min(1).max(120),
    /** Severity: PROHIBITED / LIMITED / FULLY_LIMITED / DESCRIPTIVE /
     *  BROADENING. Same convention as the db column. */
    severity: z.string().min(1).max(32),
});
/** [12.2L-121] Strategy proposal alert payload. Mirrors the card's
 *  proposalId + a tiny gist so channel adapters can render the alert
 *  without re-fetching the proposal row. monthlyBudgetUsd lets email /
 *  slack put the headline number in the preview. */
export const alertStrategyProposalPayloadSchema = z.object({
    kind: z.literal('alert.strategy_proposal'),
    ...basePayloadShape,
    proposalId: z.string().uuid(),
    proposalName: z.string().min(1).max(200),
    monthlyBudgetUsd: z.number().nonnegative(),
    channelCount: z.number().int().min(1).max(4),
});
export const alertEventCapPayloadSchema = z.object({
    kind: z.literal('alert.event_cap'),
    ...basePayloadShape,
    /** 'warn' = crossed 80% threshold (still ingesting).
     *  'reached' = hit 100% cap (ingest paused until cycle reset). */
    stage: z.enum(['warn', 'reached']),
    eventsUsed: z.number().int().min(0),
    cap: z.number().int().min(0),
    /** 0..1 — share of cap currently consumed. Same dimension as
     *  alertSpendPayload.share so the UI can reuse the same bar
     *  component if it wants. */
    share: z.number().min(0).max(2),
    /** YYYY-MM-DD — when the meter resets and the cap unlocks. */
    cycleEnd: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});
/** [s264] Mag-tokens billing payloads. Carry the numbers channel
 *  adapters need to render the alert inline (balance, threshold,
 *  pack tokens, charge amount) so the founder doesn't have to click
 *  through to /settings/billing for the basic context. */
export const billingTokensLowPayloadSchema = z.object({
    kind: z.literal('billing.tokens_low'),
    ...basePayloadShape,
    balance: z.number().int(),
    thresholdTokens: z.number().int().min(0),
    autobuyEnabled: z.boolean(),
});
export const billingTokensZeroPayloadSchema = z.object({
    kind: z.literal('billing.tokens_zero'),
    ...basePayloadShape,
    balance: z.number().int(),
    autobuyEnabled: z.boolean(),
});
export const billingAutobuyChargedPayloadSchema = z.object({
    kind: z.literal('billing.autobuy_charged'),
    ...basePayloadShape,
    packTokens: z.number().int().positive(),
    amountUsd: z.number().nonnegative(),
    /** Balance AFTER the grant landed. Reads as a positive confirmation. */
    newBalance: z.number().int(),
});
export const billingAutobuyFailedPayloadSchema = z.object({
    kind: z.literal('billing.autobuy_failed'),
    ...basePayloadShape,
    /** Best-effort reason string from Stripe — "card declined", etc.
     *  Channel adapters surface it inline so the founder knows what
     *  to fix. */
    reason: z.string().min(1).max(240),
});
export const askInputPayloadSchema = emptyPayload('ask.input');
export const askApprovalPayloadSchema = z.object({
    kind: z.literal('ask.approval'),
    ...basePayloadShape,
    /** What mag is asking the founder to approve, in one phrase. Keeps
     *  channel adapters from re-deriving the action description from
     *  the title/body. */
    action: z.string().min(1).max(280),
    /** Optional structured options — when set, the UI renders one CTA
     *  per option (approve / decline / counter). When unset, the UI
     *  falls back to the single `cta` on the notification row. */
    options: z
        .array(z.object({
        id: z.string().min(1).max(40),
        label: z.string().min(1).max(40),
        tone: z.enum(['primary', 'neutral', 'danger']).default('neutral'),
    }))
        .max(4)
        .optional(),
});
export const winMilestonePayloadSchema = z.object({
    kind: z.literal('win.milestone'),
    ...basePayloadShape,
    /** Short label for the metric — "ROAS", "Revenue", "Signups". */
    metric: z.string().min(1).max(60),
    /** Pre-formatted display string in mag's voice — "4.2× the
     *  baseline". The adapter doesn't reformat. */
    display: z.string().min(1).max(120),
    campaignId: z.string().uuid().optional(),
});
export const systemAnnouncementPayloadSchema = emptyPayload('system.announcement');
/** Discriminated union of every payload shape. Validate notification
 *  rows on insert against this so the jsonb column stays sound. */
export const notificationPayloadSchema = z.discriminatedUnion('kind', [
    authMagicLinkPayloadSchema,
    authResetPasswordPayloadSchema,
    authWelcomePayloadSchema,
    cadenceRecapMondayPayloadSchema,
    cadenceCreativeDropPayloadSchema,
    cadenceBriefFridayPayloadSchema,
    cadencePlanMonthlyPayloadSchema,
    cadenceMemoPayloadSchema,
    cadenceDailyPingPayloadSchema,
    cadenceInsightPayloadSchema,
    cadenceNudgePayloadSchema,
    cadencePostPublishedPayloadSchema,
    alertSpendPayloadSchema,
    alertAnomalyPayloadSchema,
    alertEventCapPayloadSchema,
    alertPolicyIssuePayloadSchema,
    alertStrategyProposalPayloadSchema,
    billingTokensLowPayloadSchema,
    billingTokensZeroPayloadSchema,
    billingAutobuyChargedPayloadSchema,
    billingAutobuyFailedPayloadSchema,
    askInputPayloadSchema,
    askApprovalPayloadSchema,
    winMilestonePayloadSchema,
    systemAnnouncementPayloadSchema,
]);
/* ────────────────────────────────────────────────────────────────────
 * Dispatch input — the shape callers (Mag's tool, the auth flow, the
 * cadence scheduler) hand the dispatch service. Validated server-side.
 * ──────────────────────────────────────────────────────────────────── */
export const dispatchNotificationInputSchema = z.object({
    organizationId: z.string().uuid(),
    /** Null for auth/system kinds that aren't scoped to a brand. */
    brandId: z.string().uuid().nullable(),
    userId: z.string().uuid(),
    kind: notificationKindSchema,
    /** Eyebrow line. Lowercase per §1, ≤80 chars so it works as a toast
     *  headline and an email subject base. */
    title: z.string().min(1).max(80),
    /** Mag-voice body. Newlines preserved by adapters that support them
     *  (slack, in-app); collapsed by adapters that don't. */
    body: z.string().min(1).max(4000),
    cta: notificationCtaSchema.optional(),
    priority: notificationPrioritySchema.optional(),
    payload: notificationPayloadSchema.optional(),
    /** Idempotency key. If a notification with the same (userId, kind,
     *  dedupeKey) already exists, the framework returns the existing
     *  row instead of inserting a duplicate. */
    dedupeKey: z.string().min(1).max(120).optional(),
    /** [task 3d6ade0] The language the TITLE AND BODY above are written in,
     *  supplied by whoever wrote them.
     *
     *  Optional, and omitting it keeps today's behaviour — dispatch falls back
     *  to deriving from the recipient's interface locale. That derivation is
     *  right for the many kinds whose prose is composed per-recipient, and
     *  WRONG for prose composed once against a brand: measured 2026-10-28, the
     *  two card kinds that write localized bodies had the stamp disagree with
     *  the prose on 3 of 5 notifications, in BOTH directions ('en' on Spanish
     *  text twice, 'es' on English text once).
     *
     *  Opt-in rather than a changed default on purpose. Switching dispatch to
     *  the brand-scoped resolver would fix those few and break the ~346 rows
     *  whose prose is English regardless of the brand's language — trading a
     *  small wrong answer for a large one. Only the writer knows, so only the
     *  writer should say. */
    language: z.string().min(2).max(12).optional(),
    /** [s368] The feed card this notification is about, persisted onto
     *  notifications.feed_card_id. Reading the card marks the
     *  notification read, which is what keeps an in-app-only ask from
     *  escalating to email four hours later (spec 006abee3, C3).
     *  Several payload shapes also carry a feedCardId; the column is the
     *  one the escalation gate can join on. */
    feedCardId: z.string().uuid().optional(),
    /** [task 3d1cd33] Channels this particular send should NOT use, even
     *  though the kind's catalog defaults and the founder's preferences
     *  allow them.
     *
     *  NARROWING ONLY, and never to silence. A channel named here is skipped
     *  only while at least one other channel still survives the plan —
     *  dispatch enforces that, not the caller, because a caller that got it
     *  wrong would mute a send entirely and nothing downstream would notice.
     *
     *  Exists for PAIRED SENDS. An autonomous action emits a heads-up before
     *  it fires and a receipt after, and the founder was getting both by
     *  email for the same change — 48 of 98 emails in a week were the
     *  heads-up alone. Exactly one of the pair owns the inbox now; the other
     *  stays in-app. Whichever one is suppressed still gets a notification
     *  row and a skipped delivery row, so the log remains the honest account
     *  of what was sent and what was deliberately not. */
    suppressChannels: z.array(notificationChannelSchema).max(8).optional(),
});
//# sourceMappingURL=notification.js.map